import { debounce, DebouncedFunc } from 'lodash-es'
import { pipe } from 'ramda'
import {
  CustomizationOptions,
  View,
  Widget,
} from '@ally/data-types-customization'
import { CustomizationDomain } from '@ally/data-types-host'
import { Domain } from '@ally/use-data-domain'
import { Account } from '@ally/data-types-afg-accounts'
import { CustomizeDispatch } from './CustomizeDashboardReducer'
import {
  CustomizeDataObj,
  DashboardView,
  ViewsObj,
  WidgetConfigObj,
  WidgetKey,
  WidgetObj,
} from '../types'
import { reorderWidgets } from '../utils'

export const DEBOUNCE_TIME = 3000

export const NEVER_SAVED_CUSTOMIZATIONS = 'NEVER_SAVED_CUSTOMIZATIONS'

export type SaveDataFunc = (
  payload: Partial<CustomizeDataObj>,
) => Promise<{ payload: CustomizationOptions | null }>

export function filterDashboardView(view: View | ViewsObj): boolean {
  return view.name !== 'DASHBOARD'
}

let debouncedPatch: DebouncedFunc<() => void> | null = null
let payloadForDebouncedPatch: DashboardView | null

function reset(): void {
  debouncedPatch = null
  payloadForDebouncedPatch = null
}

export function saveDashboardView(
  dispatch: CustomizeDispatch,
  payload: DashboardView,
  customizationsApiCall: SaveDataFunc,
  existingViews: View[] | ViewsObj[],
  method: 'POST' | 'PATCH',
  defaultCustomizationPayload: CustomizeDataObj | Partial<CustomizeDataObj>,
  hostDataCustomization: Domain<CustomizationDomain, []>,
): void {
  dispatch({ type: 'RESET CustomizeDashboard API alert' })
  dispatch({ type: 'set dashboardView', payload })

  if (method === 'POST') {
    dispatch({ type: 'set isSaving', payload: true })
  }

  // if the user changes the view we want to cancel the previous function call and start a new one
  if (debouncedPatch && payload !== payloadForDebouncedPatch) {
    debouncedPatch.cancel()
    reset()
    // TODO enhancement - if the user switches to a new view and back to the view they had we should not make a PATCH call since nothing really changed
  }

  if (!debouncedPatch) {
    payloadForDebouncedPatch = payload
    debouncedPatch = debounce(async () => {
      const payloadData = {
        channel: 'WEB',
        // if first time customizing, we need to add all fields for POST
        ...(method === 'POST' ? defaultCustomizationPayload : {}),
        views: [
          ...existingViews.filter(filterDashboardView),
          {
            name: 'DASHBOARD',
            value: payload,
          },
        ],
      }

      try {
        const response = await customizationsApiCall(payloadData)

        // this will only exist if a POST request was made
        const customizationId =
          (response?.payload as CustomizationOptions)?.customizationId ?? null

        if (method === 'POST') {
          dispatch({
            type: 'set customizeApiData',
            payload: {
              ...payloadData,
              ...(customizationId ? { customizationId } : {}),
            },
          })
        }

        hostDataCustomization.update()
      } catch (error) {
        dispatch({
          type: 'CustomizeDashboard API alert',
          payload: {
            shouldRender: true,
            type: 'error',
            isDashboardError: true,
          },
        })
      }
      dispatch({ type: 'set isSaving', payload: false })
      reset()
    }, DEBOUNCE_TIME)
  }
  debouncedPatch()
}

export async function saveCustomizationsUtil(
  dispatch: CustomizeDispatch,
  payload: CustomizeDataObj | Partial<CustomizeDataObj>,
  saveApiCall: SaveDataFunc,
): Promise<void> {
  dispatch({ type: 'RESET CustomizeDashboard API alert' })

  try {
    dispatch({ type: 'set isSaving', payload: true })
    const response = await saveApiCall({ ...payload, channel: 'WEB' })

    dispatch({ type: 'set isSaving', payload: false })

    // this will only exist if a POST request was made
    const customizationId =
      (response?.payload as CustomizationOptions)?.customizationId ?? null

    // sync UI with new customization data the user saved
    // if neccessary add the new customizationId if it's the user's first time saving
    dispatch({
      type: 'set customizeApiData',
      payload: {
        ...payload,
        ...(customizationId ? { customizationId } : {}),
      },
    })

    dispatch({
      type: 'CustomizeDashboard API alert',
      payload: {
        shouldRender: true,
        type: 'success',
        isDashboardError: false,
      },
    })
  } catch (error) {
    dispatch({ type: 'set isSaving', payload: false })
    dispatch({
      type: 'CustomizeDashboard API alert',
      payload: {
        shouldRender: true,
        type: 'error',
        isDashboardError: false,
      },
    })
  }
}

export function getDashboardView(
  resp: CustomizationOptions | undefined,
): DashboardView {
  const customizedView = resp?.views.find(({ name }) => name === 'DASHBOARD')
    ?.value
  return customizedView
    ? (customizedView as DashboardView)
    : // this scenario seems impossible so ignoring coverage
      /* istanbul ignore next */
      DashboardView.DEFAULT
}

// Eligible if account is open, not IRA, and CD is unfunded
export function hasTransferAccounts(openDepositAccounts: Account[]): boolean {
  const filterFundedCds = openDepositAccounts.filter(account => {
    if (account.type !== 'CDA') {
      return true
    }
    const isCD = account.type === 'CDA'
    return isCD && account.balance?.current === 0
  })
  return !filterFundedCds.every(account => !!account.indicator?.retirement)
}

export function getEligibleWidgets(widgets: WidgetConfigObj): WidgetConfigObj {
  return Object.keys(widgets).reduce((accum, widgetId) => {
    const key = widgetId as WidgetKey
    if (widgets[key]?.isEligible()) {
      return {
        ...accum,
        [widgetId]: widgets[key],
      }
    }
    return { ...accum }
  }, {} as WidgetConfigObj)
}

type WidgetFunc = (widget: Widget) => Widget

/** Default widgets on function */
export function getWidgets({
  eligibleWidgets,
  widgetsFromApi,
}: {
  eligibleWidgets: Partial<Record<WidgetKey, WidgetObj>>
  widgetsFromApi: Widget[]
}): Widget[] {
  const additionalEligibleWidgets: Widget[] = Object.keys(eligibleWidgets)
    // filter out currently added widgets
    .filter(widgetName => !widgetsFromApi.find(w => w.name === widgetName))
    // add enabled prop
    .map(widgetName => ({
      name: widgetName,
      // order gets modified below in `reorderWidgets`
      order: 0,
      enabled: true,
    }))

  // The database may still contain widgets that don't have an enabled property since this was added after an API update,
  // widgets without an enabled prop should be assumed `enabled: true`
  const forceEnabled: WidgetFunc = w => {
    const enabled = w.enabled === undefined ? true : w.enabled
    return { ...w, enabled }
  }

  const disableIneligibleWidgets: WidgetFunc = widget => {
    if (
      eligibleWidgets[widget.name as WidgetKey] === undefined &&
      widget.enabled
    ) {
      return { ...widget, enabled: false }
    }
    return widget
  }

  return reorderWidgets(
    widgetsFromApi
      .map(pipe(forceEnabled, disableIneligibleWidgets))
      .concat(additionalEligibleWidgets),
  )
}
